import React, { useState, useEffect, useCallback } from "react";
import {
  TbStack2, TbTarget, TbEye, TbEyeOff, TbLock, TbWorld, TbSettings, TbShieldCheck,
} from "react-icons/tb";
import { api } from "../../../lib/api";
import { Button, Field, Alert } from "../../../components";

// GoHighLevel.
//
// Lifted out of IntegrationsScreen unchanged when that screen became a catalogue —
// same requests, same field names, same order. The shape follows the previous
// interface deliberately: contact sync and then opportunity upsert, because that is
// the order the two things happen in when a lead unlocks a report.
//
// The field names below are the ones the API actually reads — /ghl/integrate takes a
// `ghl_`-prefixed body, which is easy to get wrong because /ghl/config answers with
// unprefixed keys.

const API_VERSIONS = ["2021-07-28", "2021-04-15"];
const DEFAULT_SOURCE = "Pipsqueak Lead Magnet";

const GoHighLevelPanel = ({ isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [pipelines, setPipelines] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [pipelineError, setPipelineError] = useState("");

  const [form, setForm] = useState({
    enabled: false,
    api_token: "",
    location_id: "",
    api_version: API_VERSIONS[0],
    custom_field_key: "",
    pipeline_id: "",
    pipeline_stage_id: "",
    source: DEFAULT_SOURCE,
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    try {
      // Flat response, not { config }. `configured` false simply means this tenant has
      // never saved one — an empty form, not an error.
      const d = await api.get("s2/api/ghl/config");
      if (d?.configured) {
        setForm({
          enabled: !!d.enabled,
          api_token: d.api_token || "",
          location_id: d.location_id || "",
          api_version: d.api_version || API_VERSIONS[0],
          custom_field_key: d.custom_field_key || "",
          pipeline_id: d.pipeline_id || "",
          pipeline_stage_id: d.pipeline_stage_id || "",
          source: d.source || DEFAULT_SOURCE,
        });
      }
      setError("");
    } catch (e) {
      setError(e.message || "Could not load integration settings.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fetchPipelines = async () => {
    if (!form.api_token || !form.location_id) {
      setPipelineError("Enter the API token and Location ID first.");
      return;
    }
    setLoadingPipelines(true); setPipelineError("");
    try {
      const d = await api.post("s2/api/ghl/pipelines", {
        api_token: form.api_token,
        location_id: form.location_id,
        api_version: form.api_version,
      });
      const list = d?.pipelines || [];
      setPipelines(list);
      if (list.length === 0) setPipelineError("No pipelines found for this location.");
    } catch (e) {
      setPipelineError(e.message || "Could not fetch pipelines from GoHighLevel.");
    } finally { setLoadingPipelines(false); }
  };

  const save = async () => {
    setBusy(true); setError(""); setSaved("");
    try {
      await api.post("s2/api/ghl/integrate", {
        ghl_api_token: form.api_token,
        ghl_location_id: form.location_id,
        ghl_api_version: form.api_version,
        ghl_custom_field_key: form.custom_field_key || null,
        ghl_pipeline_id: form.pipeline_id || null,
        ghl_pipeline_stage_id: form.pipeline_stage_id || null,
        ghl_source: form.source,
        ghl_enabled: form.enabled,
      });
      setSaved("GoHighLevel settings saved.");
      await load();
      setTimeout(() => setSaved(""), 3000);
    } catch (e) {
      setError(e.message || "Could not save. Check the token and Location ID.");
    } finally { setBusy(false); }
  };

  if (loading) return <p className="v2-hint">Loading…</p>;

  const ready = !!form.api_token && !!form.location_id;
  const stages = pipelines.find((p) => p.id === form.pipeline_id)?.stages || [];
  // A pipeline without a stage cannot create an opportunity, so it is worth saying so
  // before the save rather than letting GHL reject it later.
  const stageMissing = !!form.pipeline_id && !form.pipeline_stage_id;

  return (
    <div className="v2-settings-form v2-ghl">
      <Alert>{error}</Alert>
      {saved && <Alert tone="success">{saved}</Alert>}

      {/* Contact sync ---------------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--teal"><TbStack2 size={18} /></span>
          <div>
            <div className="v2-setting-name">GoHighLevel Integration</div>
            <div className="v2-setting-desc">Automatically sync leads as contacts and create opportunities</div>
          </div>
          <label className="v2-ghl-toggle" style={{ marginLeft: "auto" }}>
            <input type="checkbox" checked={form.enabled} disabled={!isAdmin}
                   aria-label="GoHighLevel integration active"
                   onChange={(e) => set({ enabled: e.target.checked })} />
            <span>{form.enabled ? "ACTIVE" : "INACTIVE"}</span>
          </label>
        </div>

        <div className={`v2-ghl-status${ready && form.enabled ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {!form.enabled
            ? "Inactive — leads will not be sent to GoHighLevel"
            : ready
              ? "Ready to sync — contacts will be upserted on lead capture"
              : "Add an API token and Location ID to start syncing"}
        </div>

        <Field label={<><TbLock size={13} /> API Access Token *</>}>
          <div className="v2-ghl-secret">
            <input className="v2-input" type={showToken ? "text" : "password"}
                   value={form.api_token} disabled={!isAdmin}
                   aria-label="API access token"
                   onChange={(e) => set({ api_token: e.target.value })} />
            <button type="button" className="v2-ghl-reveal"
                    aria-label={showToken ? "Hide token" : "Show token"}
                    onClick={() => setShowToken((v) => !v)}>
              {showToken ? <TbEyeOff size={16} /> : <TbEye size={16} />}
            </button>
          </div>
          <p className="v2-ghl-help">
            <b>How to get it:</b> Go to GHL Settings → Private integrations → Create New
            Integration. Select <code>contacts.readonly</code> and <code>contacts.write</code>,
            plus <code>opportunities.readonly</code> and <code>opportunities.write</code> to
            create opportunities in a pipeline.
          </p>
        </Field>

        <div className="v2-grid2">
          {/* The guidance goes under the box, not over it: Field's own `hint` renders
              above the control, which reads as a second label. */}
          <Field label={<><TbWorld size={13} /> Location ID *</>}>
            <input className="v2-input" value={form.location_id} disabled={!isAdmin}
                   aria-label="Location ID"
                   onChange={(e) => set({ location_id: e.target.value })} />
            <p className="v2-ghl-help">Find your Location ID in GoHighLevel under Settings → Business Profile</p>
          </Field>
          <Field label={<><TbSettings size={13} /> API Version</>}>
            <select className="v2-input" value={form.api_version} disabled={!isAdmin}
                    aria-label="API version"
                    onChange={(e) => set({ api_version: e.target.value })}>
              {API_VERSIONS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <p className="v2-ghl-help">Header parameter required by GoHighLevel API</p>
          </Field>
        </div>

        <Field label="Scanned Domain Custom Field Key (Optional)">
          <input className="v2-input" value={form.custom_field_key} disabled={!isAdmin}
                 aria-label="Scanned domain custom field key"
                 placeholder="scanned_domain_report"
                 onChange={(e) => set({ custom_field_key: e.target.value })} />
          <div className="v2-ghl-help">
            <b>To save the scanned domain link in GHL:</b>
            <ol>
              <li>Go to <b>Contacts → Manage fields → Add Custom field</b></li>
              <li>Use <b>Text Input single line</b></li>
              <li>Enter a Name, select <b>Contact</b> for the Object</li>
              <li>Save and enter the generated <b>Key</b> here</li>
            </ol>
          </div>
        </Field>
      </section>

      {/* Opportunity upsert ---------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--violet"><TbTarget size={18} /></span>
          <div>
            <div className="v2-setting-name">Upsert Opportunities</div>
            <div className="v2-setting-desc">Automatically create an opportunity in your GoHighLevel pipeline</div>
          </div>
        </div>

        <div className={`v2-ghl-status${form.pipeline_id && form.pipeline_stage_id ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {form.pipeline_id && form.pipeline_stage_id
            ? "Pipeline sync active — opportunities will be created for each lead"
            : "Pick a pipeline and stage to create opportunities alongside contacts"}
        </div>

        {/* A token made from the old contacts-only instructions gets a 401 "not authorized
            for this scope" here, so spell out the fix for an existing integration. */}
        <div className="v2-ghl-help">
          <b>Your token needs the opportunities permissions:</b>
          <ol>
            <li>In GHL, open the same sub-account and go to <b>Settings → Private integrations</b></li>
            <li>Open your Pipsqueak integration and also select <code>opportunities.readonly</code> and <code>opportunities.write</code></li>
            <li>Save. If GHL shows a new token, paste it into <b>API Access Token</b> above</li>
            <li>Click <b>Fetch Pipelines &amp; Stages</b>, pick a pipeline and stage, then save</li>
          </ol>
        </div>

        <div>
          <Button variant="primary" onClick={fetchPipelines}
                  disabled={loadingPipelines || !isAdmin || !ready}>
            {loadingPipelines ? "Fetching…" : "Fetch Pipelines & Stages"}
          </Button>
          {!ready && (
            <p className="v2-hint" style={{ marginTop: ".375rem" }}>
              The token and Location ID above are used to look these up.
            </p>
          )}
        </div>

        {pipelineError && <Alert tone="warning">{pipelineError}</Alert>}

        <div className="v2-grid2">
          <Field label="Pipeline">
            <select className="v2-input" value={form.pipeline_id} disabled={!isAdmin || pipelines.length === 0}
                    aria-label="Pipeline"
                    onChange={(e) => set({ pipeline_id: e.target.value, pipeline_stage_id: "" })}>
              <option value="">{pipelines.length ? "Select a Pipeline" : "Fetch pipelines first"}</option>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Pipeline Stage"
                 error={stageMissing ? "Pick a stage — a pipeline without one cannot create an opportunity." : ""}>
            <select className="v2-input" value={form.pipeline_stage_id} aria-label="Pipeline stage"
                    disabled={!isAdmin || !form.pipeline_id}
                    onChange={(e) => set({ pipeline_stage_id: e.target.value })}>
              <option value="">{form.pipeline_id ? "Select a Stage" : "Select a pipeline first"}</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        </div>

        {/* A saved pipeline that predates this session has an id but no fetched list to
            name it from. Say so, rather than rendering an empty select that looks like
            the setting was lost. */}
        {form.pipeline_id && pipelines.length === 0 && (
          <p className="v2-hint">
            A pipeline is already saved. Fetch pipelines to see its name or change it.
          </p>
        )}
      </section>

      <div className="v2-ghl-note">
        <TbShieldCheck size={15} />
        <span>
          <b>How it works:</b> When a lead unlocks their report, their contact information is
          automatically synchronized with GoHighLevel.
        </span>
      </div>

      <div>
        <Button variant="primary" onClick={save}
                disabled={busy || !isAdmin || !form.api_token || !form.location_id}>
          {busy ? "Saving…" : "Save GoHighLevel settings"}
        </Button>
      </div>
    </div>
  );
};

export default GoHighLevelPanel;
